import * as z from 'zod';

import { addressQuerySchema } from '@/_containers/inputs/address/shared';
import { competitorQueryLinkerSchema } from '@/_pages/competitors/shared/schemas/competitor-query-linker-schema';
import { agreementTableSchema, contactLinkerWithPhoneNumberQuerySchema } from '@/_pages/contacts/shared/schemas';
import { domainQueryLinkerSchemaWithExData } from '@/_pages/domains/shared/schemas';
import { audienceQueryLinkerSchema } from '@/_pages/settings/audiences/shared/schemas';
import { billingSystemQueryLinkerSchema } from '@/_pages/settings/billing-systems/shared';
import { billingTermQueryLinkerSchema } from '@/_pages/settings/billing-terms/shared/billing-term-query-linker-schema';
import { brandQueryLinkerSchema } from '@/_pages/settings/brands/shared/schemas/brand-query-linker-schema';
import { industryQueryLinkerSchema } from '@/_pages/settings/industries/shared';
import { magazineQueryLinkerSchema } from '@/_pages/settings/magazines/shared';
import { organizationTypeQueryLinkerSchema } from '@/_pages/settings/organization-types/shared';
import { partnerQueryLinkerSchema } from '@/_pages/settings/partners/shared';
import { relationshipScoreQueryLinkerSchema } from '@/_pages/settings/relationship-scores/shared/schemas';
import { companyPivotSchema } from '@/_schemas/companies/company-pivot-schema';
import { companyQueryLinkerSchema } from '@/_schemas/companies/company-query-linker-schema';
import { nullableNumericQuerySchema, paginatedResponseSchema } from '@/_schemas/generic';
import { phoneNumberQuerySchema } from '@/_schemas/phone-numbers';
import { statusQuerySchema } from '@/_schemas/status-input';
import { teamMemberQueryLinkerSchema } from '@/_schemas/team-members/team-members-query-linker-schema';

import { marketingCampaignTableSchema } from './linked-schemas';

const baseCompanyQuerySchema = z.object({
  ...companyQueryLinkerSchema.shape,
  status: statusQuerySchema,
  short_name: z.string().nullable(),
  consumers: z.number().nullable(),
  year_established: z.number().nullable(),
  annual_meeting_month: z.string().nullable(),
  zoom_meeting_code: z.string().nullable(),
});

const webProviderSchema = z.union([brandQueryLinkerSchema, competitorQueryLinkerSchema]);
export const companyWebProviderTypeSchema = z.union([z.literal('brand'), z.literal('competitor')]);
export type CompanyWebProviderTypeSchema = z.infer<typeof companyWebProviderTypeSchema>;

export const companyQuerySchema = z.object({
  ...baseCompanyQuerySchema.shape,
  contacts_count: z.number().nullish(),
  timezone_id: z.number().nullish(),
  tax_id: z.string().nullable(),
  exempt: z.string().nullable(),

  /* keeping theme for revision history */
  gnt_affiliations: z.array(companyQueryLinkerSchema).nullable(),
  non_gnt_affiliations: z.array(companyQueryLinkerSchema).nullable(),
  /* keeping theme for revision history */

  all_gnt_affiliations: z.array(companyQueryLinkerSchema).nullable(),
  all_non_gnt_affiliations: z.array(companyQueryLinkerSchema).nullable(),

  subsidiaries: z.array(companyQueryLinkerSchema).nullable(),
  primary_subsidiaries: z.array(companyQueryLinkerSchema).nullish(),

  phone_numbers: z.array(phoneNumberQuerySchema).nullable(),
  brand_id: z.number().nullable(),
  brand: brandQueryLinkerSchema.nullable(),
  partner_id: z.number().nullable(),
  partner: partnerQueryLinkerSchema.nullable(),
  relationship_score_id: z.number().nullable(),
  relationship_score: relationshipScoreQueryLinkerSchema.nullable(),
  audience_id: z.number().nullable(),
  audience: audienceQueryLinkerSchema.nullable(),
  organization_type_id: z.number().nullable(),
  organization_type: organizationTypeQueryLinkerSchema.nullable(),
  billing_system_id: z.number().nullable(),
  billing_system: billingSystemQueryLinkerSchema.nullable(),
  industries: z.array(industryQueryLinkerSchema).nullish(),
  magazine_id: z.number().nullable(),
  magazine: magazineQueryLinkerSchema.nullable(),
  account_manager_id: z.number().nullable(),
  // team-members
  account_manager: teamMemberQueryLinkerSchema.nullable(),
  contacts: z.array(contactLinkerWithPhoneNumberQuerySchema).nullable(),
  domains: z.array(domainQueryLinkerSchemaWithExData).nullable(),
  addresses: z.array(addressQuerySchema).nullable(),
  pivot: companyPivotSchema.nullish(),
  primary_contact: z.array(contactLinkerWithPhoneNumberQuerySchema).nullish(),
  alternate_contacts: z.array(contactLinkerWithPhoneNumberQuerySchema).nullish(),

  current_web_provider_id: z.number().nullable(),
  current_web_provider: webProviderSchema.nullish(),
  current_web_provider_type: companyWebProviderTypeSchema.nullable(),

  billing_term_id: z.number().nullable(),
  billing_term: billingTermQueryLinkerSchema.nullable(),

  harvest: nullableNumericQuerySchema(),
  box: nullableNumericQuerySchema(),
  freshdesk: nullableNumericQuerySchema(),

  agreements: z.array(agreementTableSchema.omit({ roles: true })),
  marketing_campaigns: z.array(marketingCampaignTableSchema).nullable(),

  client_assigned_vendor_id: z.string().nullable(),
  override_payable_name: z.string().nullable(),
});

export type CompanyQuerySchema = z.infer<typeof companyQuerySchema>;

export const companyQueryAllSchema = paginatedResponseSchema(companyQuerySchema);

export type CompanyQueryAllSchema = z.infer<typeof companyQueryAllSchema>;
